import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { strategies } from '../data/registry';
import { GOD_GLYPHS } from '../data/godGlyphs';
import { FAMILIES } from '../data/taxonomy';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../utils/translations';

const GOLD = '#F4C95D';
const MONO = "'JetBrains Mono', monospace";
const RUNIC = "'Cinzel', 'Bebas Neue', serif";
const DISPLAY = "'Bebas Neue', sans-serif";
const EASE = [0.23, 1, 0.32, 1];
const CONSTELLATION_MIN = 3; // families with at least this many gods fold into one coffer

const CX = 410;
const CY = 410;

const polar = (r, a) => [CX + r * Math.cos(a), CY + r * Math.sin(a)];
function sector(r0, r1, a0, a1) {
  const [x0, y0] = polar(r1, a0);
  const [x1, y1] = polar(r1, a1);
  const [x2, y2] = polar(r0, a1);
  const [x3, y3] = polar(r0, a0);
  return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${r1} ${r1} 0 0 1 ${x1.toFixed(1)} ${y1.toFixed(1)} L${x2.toFixed(1)} ${y2.toFixed(1)} A${r0} ${r0} 0 0 0 ${x3.toFixed(1)} ${y3.toFixed(1)} Z`;
}
const frac = (x) => x - Math.floor(x);
const star = (s) => `M0 ${-s} L${s * 0.28} ${-s * 0.28} L${s} 0 L${s * 0.28} ${s * 0.28} L0 ${s} L${-s * 0.28} ${s * 0.28} L${-s} 0 L${-s * 0.28} ${-s * 0.28} Z`;

/**
 * The pantheon seen from below, like the dome in Rome: one coffer per god around the oculus, each with
 * the symbol of its god carved in gold. A family with many gods does not crowd the ring: it hangs as a
 * single constellation (one star per god) that unfolds into its own ring when clicked. Pointing at a
 * coffer lights it and tells, beside the dome, what that god is for; clicking a god opens the strategy.
 */
export default function PantheonDome() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const gods = useMemo(() => strategies.filter((s) => GOD_GLYPHS[s.slug]), []);
  const [open, setOpen] = useState(null);     // family id whose constellation is unfolded
  const [active, setActive] = useState(null); // slug of a god, or `family:<id>`

  const families = useMemo(() => FAMILIES.map((f) => ({ ...f, gods: gods.filter((g) => g.familyId === f.id) })).filter((f) => f.gods.length >= CONSTELLATION_MIN), [gods]);
  const ring = useMemo(() => {
    if (open) return (families.find((f) => f.id === open)?.gods || []).map((g) => ({ kind: 'god', key: g.slug, god: g }));
    const folded = new Set(families.flatMap((f) => f.gods.map((g) => g.slug)));
    const items = gods.filter((g) => !folded.has(g.slug)).map((g) => ({ kind: 'god', key: g.slug, god: g }));
    families.forEach((f) => items.push({ kind: 'family', key: `family:${f.id}`, family: f }));
    return items;
  }, [gods, families, open]);
  const n = ring.length;

  const currentGod = gods.find((g) => g.slug === active) || null;
  const currentFamily = active?.startsWith('family:') ? families.find((f) => `family:${f.id}` === active) : null;
  const openFamily = families.find((f) => f.id === open) || null;
  const accent = currentGod ? currentGod.color : currentFamily ? GOLD : 'rgba(255,255,255,0.08)';

  const unfold = (id) => { setOpen(id); setActive(null); };
  const fold = () => { setOpen(null); setActive(null); };

  return (
    <div className="pantheon-dome" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)', gap: '32px', alignItems: 'center' }}>
      <div>
        <div style={{ fontFamily: MONO, fontSize: '11px', color: '#00ffc8', letterSpacing: '3px', marginBottom: '10px' }}>{t('home.pantheon.kicker', language)}</div>
        <h2 style={{ fontFamily: DISPLAY, fontSize: '48px', color: '#fff', letterSpacing: '2px', lineHeight: 1, margin: '0 0 12px' }}>{t('home.pantheon.title', language)}</h2>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '15px', color: '#888', lineHeight: 1.65, margin: 0, maxWidth: '46ch' }}>{t('home.pantheon.lead', language)}</p>

        <div className="pantheon-dome-info" aria-live="polite" style={{ marginTop: '28px', minHeight: '168px', borderLeft: `2px solid ${accent}`, paddingLeft: '18px', transition: 'border-color 200ms ease' }}>
          {openFamily && (
            <button type="button" onClick={fold} style={{ background: 'none', border: 0, padding: 0, marginBottom: '14px', cursor: 'pointer', fontFamily: MONO, fontSize: '11px', letterSpacing: '1px', color: '#e2c58a' }}>
              ← {t('home.pantheon.back', language)}
            </button>
          )}
          {currentGod ? (
            <motion.div key={currentGod.slug} initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: EASE }}>
              <div style={{ fontFamily: RUNIC, fontWeight: 600, fontSize: '22px', letterSpacing: '4px', color: '#e2c58a' }}>{currentGod.name}</div>
              <div style={{ fontFamily: MONO, fontSize: '10px', color: currentGod.color, letterSpacing: '1.5px', margin: '6px 0 10px' }}>{t(`riskProfiles.${currentGod.riskProfile}`, language)}</div>
              <p style={{ color: '#bbb', fontSize: '14px', lineHeight: 1.6, margin: '0 0 12px', maxWidth: '44ch' }}>{t(`home.pantheon.items.${currentGod.slug}`, language)}</p>
              <Link to={`/strategies/${currentGod.slug}`} style={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '1px', color: currentGod.color, textDecoration: 'none' }}>{t('strategies.openCard', language)}</Link>
            </motion.div>
          ) : currentFamily ? (
            <motion.div key={currentFamily.id} initial={reduce ? false : { opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: EASE }}>
              <div style={{ fontFamily: RUNIC, fontWeight: 600, fontSize: '22px', letterSpacing: '4px', color: '#e2c58a' }}>{t(`taxonomy.families.${currentFamily.id}`, language)}</div>
              <div style={{ fontFamily: MONO, fontSize: '10px', color: GOLD, letterSpacing: '1.5px', margin: '6px 0 10px' }}>{t('home.pantheon.familyCount', language).replace('{n}', currentFamily.gods.length)}</div>
              <p style={{ color: '#bbb', fontSize: '14px', lineHeight: 1.6, margin: '0 0 12px', maxWidth: '44ch' }}>{t(`home.pantheon.constellations.${currentFamily.id}`, language)}</p>
              <button type="button" onClick={() => unfold(currentFamily.id)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: MONO, fontSize: '11px', letterSpacing: '1px', color: GOLD }}>{t('home.pantheon.unfold', language)}</button>
            </motion.div>
          ) : (
            <p style={{ color: '#666', fontSize: '13px', lineHeight: 1.6, margin: 0, maxWidth: '40ch' }}>{t(openFamily ? 'home.pantheon.hintOpen' : 'home.pantheon.hint', language)}</p>
          )}
        </div>
      </div>

      <svg viewBox="0 0 820 820" width="100%" role="group" aria-label={t('home.pantheon.title', language)} style={{ display: 'block', maxWidth: '760px', margin: '0 auto' }}>
        <defs>
          <filter id="dome-relief" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="5" result="b" />
            <feSpecularLighting in="b" surfaceScale="9" specularConstant="1" specularExponent="20" lightingColor="#fff4cf" result="s">
              <feDistantLight azimuth="225" elevation="46" />
            </feSpecularLighting>
            <feComposite in="s" in2="SourceAlpha" operator="in" result="s2" />
            <feDiffuseLighting in="b" surfaceScale="9" diffuseConstant="1.05" lightingColor="#e0ae4c" result="d">
              <feDistantLight azimuth="225" elevation="52" />
            </feDiffuseLighting>
            <feComposite in="d" in2="SourceAlpha" operator="in" result="d2" />
            <feMerge><feMergeNode in="d2" /><feMergeNode in="s2" /></feMerge>
          </filter>
          <filter id="dome-soft"><feGaussianBlur stdDeviation="18" /></filter>
          <filter id="dome-star-glow" x="-100%" y="-100%" width="300%" height="300%"><feGaussianBlur stdDeviation="2.2" /></filter>
          <radialGradient id="dome-stone" cx="50%" cy="50%" r="60%"><stop offset="0" stopColor="#3b2a12" /><stop offset="100%" stopColor="#17100a" /></radialGradient>
          <radialGradient id="dome-night" cx="50%" cy="50%" r="60%"><stop offset="0" stopColor="#141a2e" /><stop offset="100%" stopColor="#0a0a14" /></radialGradient>
          <radialGradient id="dome-oculus" cx="50%" cy="50%" r="50%"><stop offset="0" stopColor="#fff6d6" /><stop offset="35%" stopColor={GOLD} stopOpacity="0.85" /><stop offset="100%" stopColor={GOLD} stopOpacity="0" /></radialGradient>
          <style>{`
            .dome-breath { animation: dome-breath 6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
            @keyframes dome-breath { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.5; } }
            .dome-coffer { cursor: pointer; outline: none; }
            .dome-coffer .dome-glyph { transition: opacity 200ms cubic-bezier(0.23, 1, 0.32, 1); opacity: 0.86; }
            .dome-coffer .dome-rim { transition: stroke-opacity 200ms cubic-bezier(0.23, 1, 0.32, 1), stroke-width 200ms cubic-bezier(0.23, 1, 0.32, 1); }
            .dome-coffer .dome-wash { transition: opacity 200ms cubic-bezier(0.23, 1, 0.32, 1); opacity: 0; }
            .dome-coffer.is-active .dome-glyph, .dome-coffer:focus-visible .dome-glyph { opacity: 1; }
            .dome-coffer.is-active .dome-rim, .dome-coffer:focus-visible .dome-rim { stroke-opacity: 0.95; stroke-width: 2; }
            .dome-coffer.is-active .dome-wash, .dome-coffer:focus-visible .dome-wash { opacity: 0.16; }
            .dome-star { animation: dome-twinkle 3.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
            @keyframes dome-twinkle { 0%, 100% { opacity: 0.7; transform: scale(0.9); } 50% { opacity: 1; transform: scale(1.12); } }
            .dome-coffer .dome-lines { transition: stroke-opacity 300ms cubic-bezier(0.23, 1, 0.32, 1); }
            .dome-coffer.is-active .dome-lines, .dome-coffer:focus-visible .dome-lines { stroke-opacity: 0.9; }
            .dome-oculus-link { cursor: pointer; outline: none; }
            @media (prefers-reduced-motion: reduce) { .dome-breath, .dome-star { animation: none; } }
          `}</style>
        </defs>

        <circle cx={CX} cy={CY} r="392" fill="#120c07" stroke={GOLD} strokeOpacity="0.5" strokeWidth="1.5" />
        {Array.from({ length: 24 }, (_, i) => {
          const a0 = (i / 24) * Math.PI * 2 - Math.PI / 2 + 0.02;
          const a1 = ((i + 1) / 24) * Math.PI * 2 - Math.PI / 2 - 0.02;
          return (
            <g key={i}>
              <path d={sector(338, 384, a0, a1)} fill="#1d140a" stroke={GOLD} strokeOpacity="0.28" />
              <path d={sector(348, 374, a0 + 0.03, a1 - 0.03)} fill="#0e0905" stroke="#000" strokeOpacity="0.6" />
            </g>
          );
        })}

        <AnimatePresence mode="wait" initial={false}>
          <motion.g key={open || 'all'} initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={reduce ? undefined : { opacity: 0 }} transition={{ duration: 0.28, ease: EASE }}>
            {ring.map((item, i) => {
              const a0 = (i / n) * Math.PI * 2 - Math.PI / 2 + 0.03;
              const a1 = ((i + 1) / n) * Math.PI * 2 - Math.PI / 2 - 0.03;
              const am = (a0 + a1) / 2;
              const [sx, sy] = polar(234, am);
              const deg = (am * 180) / Math.PI + 90;
              const flip = Math.sin(am) > 0.2;
              const [tx, ty] = polar(flip ? 303 : 296, am);
              const inner = sector(166, 314, a0 + 0.035, a1 - 0.035);
              const isFamily = item.kind === 'family';
              const label = isFamily ? t(`taxonomy.families.${item.family.id}`, language).toUpperCase() : item.god.name;
              const color = isFamily ? GOLD : item.god.color;
              const go = () => (isFamily ? unfold(item.family.id) : navigate(`/strategies/${item.god.slug}`));
              let stars = null;
              if (isFamily) {
                const pts = item.family.gods.map((g, k) => {
                  const r = 192 + 100 * frac(k * 0.6180339887 + 0.21);
                  const a = a0 + 0.085 + (a1 - a0 - 0.17) * frac(k * 0.3819660113 + 0.37);
                  const [x, y] = polar(r, a);
                  return { x, y, s: 4.2 + 3.2 * frac(k * 0.7548776662 + 0.11), delay: frac(k * 0.43) * 3.6, slug: g.slug };
                });
                const d = pts.map((p, k) => `${k ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
                stars = (
                  <g>
                    <path className="dome-lines" d={d} fill="none" stroke={GOLD} strokeOpacity="0.4" strokeWidth="0.8" strokeLinejoin="round" />
                    {pts.map((p) => (
                      <g key={p.slug} transform={`translate(${p.x.toFixed(1)} ${p.y.toFixed(1)})`}>
                        <path d={star(p.s * 1.9)} fill={GOLD} opacity="0.35" filter="url(#dome-star-glow)" />
                        <path className="dome-star" d={star(p.s)} fill="#fff1c2" style={{ animationDelay: `${p.delay.toFixed(2)}s` }} />
                      </g>
                    ))}
                  </g>
                );
              }
              return (
                <motion.g
                  key={item.key}
                  className={`dome-coffer${active === item.key ? ' is-active' : ''}`}
                  role={isFamily ? 'button' : 'link'}
                  tabIndex={0}
                  aria-label={label}
                  aria-expanded={isFamily ? false : undefined}
                  initial={reduce ? false : { opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.45, delay: i * 0.06, ease: EASE }}
                  onPointerEnter={() => setActive(item.key)}
                  onFocus={() => setActive(item.key)}
                  onClick={go}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
                >
                  <path d={sector(150, 330, a0, a1)} fill="url(#dome-stone)" stroke={GOLD} strokeOpacity="0.45" strokeWidth="1.2" />
                  <path d={inner} fill={isFamily ? 'url(#dome-night)' : '#0f0a06'} stroke="#000" strokeOpacity="0.7" strokeWidth="2" />
                  <path className="dome-wash" d={inner} fill={color} />
                  <path className="dome-rim" d={inner} fill="none" stroke={color} strokeOpacity="0.35" strokeWidth="1" />
                  {isFamily ? stars : (
                    <g className="dome-glyph" transform={`translate(${sx.toFixed(1)} ${sy.toFixed(1)}) scale(${94 / 512}) translate(-256 -256)`} filter="url(#dome-relief)">
                      <path d={GOD_GLYPHS[item.god.slug].d} fill="#000" />
                    </g>
                  )}
                  <text
                    x={tx.toFixed(1)}
                    y={ty.toFixed(1)}
                    transform={`rotate(${(flip ? deg + 180 : deg).toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)})`}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontFamily={RUNIC}
                    fontWeight="600"
                    fontSize="11"
                    letterSpacing="3"
                    fill="#e2c58a"
                  >
                    {label}
                  </text>
                </motion.g>
              );
            })}
          </motion.g>
        </AnimatePresence>

        <circle className="dome-breath" cx={CX} cy={CY} r="230" fill="url(#dome-oculus)" filter="url(#dome-soft)" />
        <circle cx={CX} cy={CY} r="142" fill="#0b0805" stroke={GOLD} strokeOpacity="0.7" strokeWidth="2" />
        <circle cx={CX} cy={CY} r="120" fill="url(#dome-oculus)" />
        <circle cx={CX} cy={CY} r="120" fill="none" stroke={GOLD} strokeWidth="1.5" />
        {openFamily ? (
          <g className="dome-oculus-link" role="button" tabIndex={0} aria-label={t('home.pantheon.back', language)} onClick={fold} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fold(); } }}>
            <circle cx={CX} cy={CY} r="118" fill="transparent" />
            <text x={CX} y={CY - 4} textAnchor="middle" fontFamily={RUNIC} fontWeight="700" fontSize="22" letterSpacing="6" fill="#2a1b08">{t(`taxonomy.families.${openFamily.id}`, language).toUpperCase()}</text>
            <text x={CX} y={CY + 20} textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="3" fill="#4a3210">← {t('home.pantheon.back', language).toUpperCase()}</text>
          </g>
        ) : (
          <Link to="/yggdrasil" aria-label="YGGDRASIL">
            <circle cx={CX} cy={CY} r="118" fill="transparent" />
            <text x={CX} y={CY - 2} textAnchor="middle" fontFamily={RUNIC} fontWeight="700" fontSize="30" letterSpacing="8" fill="#2a1b08">HMA</text>
            <text x={CX} y={CY + 22} textAnchor="middle" fontFamily={MONO} fontSize="10" letterSpacing="4" fill="#4a3210">YGGDRASIL</text>
          </Link>
        )}
      </svg>
    </div>
  );
}
